import { Helmet } from "react-helmet-async";

const SITE_NAME = "OFALycée Éditorial";
const SITE_DESCRIPTION = "Le média de l'éducation bilingue et internationale — par OFALycée";
const DEFAULT_IMAGE = "https://customer-assets.emergentagent.com/job_74fa0186-5442-40cb-a741-6d13f74905a7/artifacts/a3ku3uty_OFAlycee_center_logo_darkblue.png";
const SITE_LOCALE = "fr_FR";
const CANONICAL_BASE = "https://blog.ofalycee.org";

export function SEO({
  title,
  description = SITE_DESCRIPTION,
  image = DEFAULT_IMAGE,
  url,
  type = "website",
  article = null,
  noindex = false,
}) {
  const fullTitle = title ? `${title} - ${SITE_NAME}` : SITE_NAME;
  const baseUrl = CANONICAL_BASE;
  // Always build a clean canonical URL: strip utm_, _hsenc, fbclid and other tracking params
  const cleanUrl = (raw) => {
    try {
      const u = new URL(raw);
      [...u.searchParams.keys()].forEach(k => {
        if (k.startsWith("utm_") || k === "_hsenc" || k === "_hsmi" || k === "fbclid" || k === "gclid") {
          u.searchParams.delete(k);
        }
      });
      return u.pathname + (u.search || "");
    } catch { return raw; }
  };
  const canonicalUrl = url ? `${baseUrl}${url}` : `${baseUrl}${cleanUrl(window.location.href.replace(baseUrl, ""))}`;
  // Strip HTML from description (excerpt may contain rich text)
  const cleanDescription = description?.replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ").trim() || SITE_DESCRIPTION;
  // Ensure image is always an absolute URL (for LinkedIn, Facebook, Twitter)
  const absoluteImage = image && !image.startsWith("http") ? `${baseUrl}${image}` : image;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={cleanDescription} />
      <link rel="canonical" href={canonicalUrl} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={cleanDescription} />
      <meta property="og:image" content={absoluteImage} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={SITE_LOCALE} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={cleanDescription} />
      <meta name="twitter:image" content={absoluteImage} />

      {/* Article specific */}
      {article && <meta property="article:published_time" content={article.published_at} />}
      {article && <meta property="article:author" content="OFALycée" />}
      {article?.categories?.map((cat, i) => (
        <meta key={i} property="article:tag" content={cat} />
      ))}
    </Helmet>
  );
}

export function ArticleSchema({ article, url }) {
  if (!article) return null;
  const baseUrl = CANONICAL_BASE;
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt || article.title,
    image: article.featured_image?.startsWith("http")
      ? article.featured_image
      : `${baseUrl}${article.featured_image}`,
    datePublished: article.published_at,
    dateModified: article.updated_at || article.published_at,
    author: { "@type": "Organization", name: "OFALycée" },
    publisher: {
      "@type": "Organization",
      name: "OFALycée",
      logo: { "@type": "ImageObject", url: DEFAULT_IMAGE },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": `${baseUrl}${url}` },
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

export function OrganizationSchema() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    name: "OFALycée",
    url: window.location.origin,
    logo: DEFAULT_IMAGE,
    description: SITE_DESCRIPTION,
    sameAs: [],
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}

export function BreadcrumbSchema({ items }) {
  if (!items?.length) return null;
  const baseUrl = CANONICAL_BASE;
  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: item.url ? `${baseUrl}${item.url}` : undefined,
    })),
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
}
